import React, { useState, useRef, useEffect } from 'react';
import { Moon, Sun } from 'lucide-react';
import '../assets/css/nav.css';
import logoImage from '@/assets/images/logo.png';

// Script to prevent FOUC (Flash Of Unstyled Content)
const blockingSetInitialColorMode = `(function() {
  function getInitialColorMode() {
    const persistedColorPreference = window.localStorage.getItem('theme');
    const hasPersistedPreference = typeof persistedColorPreference === 'string';
    
    if (hasPersistedPreference) {
      return persistedColorPreference;
    }
    
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const hasMediaQueryPreference = typeof mql.matches === 'boolean';
    
    if (hasMediaQueryPreference) {
      return mql.matches ? 'dark' : 'light';
    }
    
    return 'light';
  }
  
  const colorMode = getInitialColorMode();
  document.documentElement.classList.toggle('dark', colorMode === 'dark');
})();`;

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isCollectionsOpen, setIsCollectionsOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const dropdownRef = useRef(null);

  // Initialize theme
  useEffect(() => {
    // Check localStorage first
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      setIsDarkMode(savedTheme === 'dark');
      document.documentElement.classList.toggle('dark', savedTheme === 'dark');
    } else {
      // Fallback to system preference
      const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      setIsDarkMode(systemPrefersDark);
      document.documentElement.classList.toggle('dark', systemPrefersDark);
      localStorage.setItem('theme', systemPrefersDark ? 'dark' : 'light');
    }

    // Listen for system preference changes
    const darkModeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleSystemPreferenceChange = (e) => {
      // Only update if no manual preference is set
      if (!localStorage.getItem('theme')) {
        const newIsDarkMode = e.matches;
        setIsDarkMode(newIsDarkMode);
        document.documentElement.classList.toggle('dark', newIsDarkMode);
      }
    };

    darkModeMediaQuery.addListener(handleSystemPreferenceChange);
    return () => darkModeMediaQuery.removeListener(handleSystemPreferenceChange);
  }, []);

  const toggleTheme = () => {
    const newIsDarkMode = !isDarkMode;
    setIsDarkMode(newIsDarkMode);
    document.documentElement.classList.toggle('dark', newIsDarkMode);
    localStorage.setItem('theme', newIsDarkMode ? 'dark' : 'light');
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsCollectionsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const collections = [
    { name: 'Pop Art', icon: '🟡', description: 'Bold colors and iconic imagery' },
    { name: 'Sci-Fi', icon: '👽', description: 'Futuristic and speculative visions' },
    { name: 'Photorealizm', icon: '📸', description: 'Stunning realistic detail' },
    { name: 'Sculptures', icon: '🗿', description: 'Three-dimensional forms' },
    { name: 'Drawings', icon: '✏️', description: 'Fine line work and sketches' },
    { name: 'Black And White', icon: '⚫', description: 'High contrast monochrome' }
  ];

  const handleNavigation = (path, linkName) => {
    console.log(`Making GET request to: ${path} (${linkName})`);
    window.location.href = path;
  };

  const generateCollectionPath = (name) => `/portfolio/${name.toLowerCase().replace(/\s+/g, '-')}`;

  return (
    <>
      <script dangerouslySetInnerHTML={{ __html: blockingSetInitialColorMode }} />

      <nav className="navbar">
        <div className="navbar-container">
          <a 
            href="/" 
            className="navbar-logo" 
            onClick={(e) => {
              e.preventDefault();
              handleNavigation('/', 'Logo - Rob Granito');
            }}
            title="Go to Homepage"
          >
            <img 
              src={logoImage} 
              alt="Rob Granito Logo" 
              className="logo-image"
              width={50}
              height={50}
            />
          </a>

          <div className="navbar-desktop">
            <a 
              href="/" 
              className="nav-link" 
              onClick={(e) => {
                e.preventDefault();
                handleNavigation('/', 'Home');
              }}
            >
              Home
            </a>
            
            <div className="dropdown-container" ref={dropdownRef}>
              <button 
                className="dropdown-button"
                onClick={() => setIsCollectionsOpen(!isCollectionsOpen)}
                aria-expanded={isCollectionsOpen}
                aria-haspopup="true"
              >
                Portfolio
                <svg className={`dropdown-icon ${isCollectionsOpen ? 'open' : ''}`} width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {isCollectionsOpen && (
                <div className="dropdown-menu">
                  <div className="dropdown-grid-single">
                    <div className="collection-section">
                      <h4 className="section-title">Art Collections</h4>
                      <ul className="collection-list">
                        {/* New "All" link for desktop */}
                        <li key="all-portfolio-desktop">
                          <a
                            href="/portfolio"
                            className="collection-link"
                            onClick={(e) => {
                              e.preventDefault();
                              handleNavigation('/portfolio', 'Portfolio: All');
                              // setIsCollectionsOpen(false); // Optional: close dropdown on click
                            }}
                          >
                            <span className="collection-icon">🖼️</span>
                            <div className="collection-text">
                              <span className="collection-name">All</span>
                              <span className="collection-description">View all portfolio items</span>
                            </div>
                            <span className="collection-arrow">→</span>
                          </a>
                        </li>
                        {collections.map((collection) => {
                          const path = generateCollectionPath(collection.name);
                          return (
                            <li key={collection.name}>
                              <a
                                href={path}
                                className="collection-link"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleNavigation(path, `Collection: ${collection.name}`);
                                  // setIsCollectionsOpen(false); // Optional: close dropdown on click
                                }}
                              >
                                <span className="collection-icon">{collection.icon}</span>
                                <div className="collection-text">
                                  <span className="collection-name">{collection.name}</span>
                                  <span className="collection-description">{collection.description}</span>
                                </div>
                                <span className="collection-arrow">→</span>
                              </a>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <a 
              href="/about" 
              className="nav-link" 
              onClick={(e) => {
                e.preventDefault();
                handleNavigation('/about', 'About');
              }}
            >
              About
            </a>
            <a 
              href="/contact" 
              className="nav-contact-button" 
              onClick={(e) => {
                e.preventDefault();
                handleNavigation('/contact', 'Contact Button');
              }}
            >
              Contact
            </a>

            <button 
              onClick={toggleTheme} 
              title="Toggle theme"
              className="theme-toggle-button"
              style={{ marginLeft: '1rem' }} 
            >
              {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
              <span className="theme-toggle-text">{isDarkMode ? 'Light' : 'Dark'} Mode</span>
            </button>
          </div>

          <button 
            className="mobile-menu-button"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-expanded={isMenuOpen}
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
          >
            {isMenuOpen ? (
              <svg className="mobile-menu-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="mobile-menu-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>

        {isMenuOpen && (
          <div className="mobile-menu">
            <MobileNavLink 
              to="/" 
              onBaseClick={() => setIsMenuOpen(false)} 
              onNavigate={handleNavigation}
              navDescription="Mobile Home"
            >
              Home
            </MobileNavLink>
            
            <MobileDropdown 
              isOpen={isCollectionsOpen} 
              toggle={() => setIsCollectionsOpen(!isCollectionsOpen)}
              collections={collections} 
              onNavigate={handleNavigation}
              generatePath={generateCollectionPath}
              onLinkClick={() => setIsMenuOpen(false)} // This closes the main mobile menu
            />
            
            <MobileNavLink 
              to="/about" 
              onBaseClick={() => setIsMenuOpen(false)} 
              onNavigate={handleNavigation}
              navDescription="Mobile About"
            >
              About
            </MobileNavLink>
            <MobileNavLink 
              to="/contact" 
              isButton 
              onBaseClick={() => setIsMenuOpen(false)} 
              onNavigate={handleNavigation}
              navDescription="Mobile Contact Button"
            >
              Contact
            </MobileNavLink>
            
            <div style={{ padding: '0.75rem 1rem', textAlign: 'center' }}>
              <button 
                onClick={() => { toggleTheme(); setIsMenuOpen(false); }} 
                title="Toggle theme"
                className="theme-toggle-button"
                style={{ width: '100%' }} 
              >
                {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
                <span className="theme-toggle-text">{isDarkMode ? 'Light' : 'Dark'} Mode</span>
              </button>
            </div>
          </div>
        )}
      </nav>
    </>
  );
};

const MobileDropdown = ({ isOpen, toggle, collections, onNavigate, generatePath, onLinkClick }) => (
  <div className="mobile-dropdown">
    <button 
      className="mobile-dropdown-button" 
      onClick={toggle} 
      aria-expanded={isOpen} 
      aria-haspopup="true"
    >
      Portfolio
      <svg className={`mobile-dropdown-icon ${isOpen ? 'open' : ''}`} width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M19 9l-7 7-7-7" />
      </svg>
    </button>
    {isOpen && (
      <div className="mobile-dropdown-content">
        {/* New "All" link for mobile */}
        <MobileNavLink
          key="all-portfolio-mobile"
          to="/portfolio"
          onBaseClick={() => {
            if (onLinkClick) onLinkClick(); // Closes main mobile menu
            // toggle(); // Optional: if you want to close the portfolio dropdown itself
          }}
          onNavigate={onNavigate}
          navDescription="Mobile Portfolio: All"
          className="mobile-collection-link"
        >
          <span className="mobile-collection-icon">🖼️</span>
          <div className="mobile-collection-text">
            <span className="mobile-collection-name">All</span>
            <span className="mobile-collection-description">View all portfolio items</span>
          </div>
        </MobileNavLink>
        {collections.map((collection) => {
          const path = generatePath(collection.name);
          return (
            <MobileNavLink 
              key={collection.name}
              to={path}
              onBaseClick={() => {
                if (onLinkClick) onLinkClick(); // Closes main mobile menu
                // toggle(); // Optional: if you want to close the portfolio dropdown itself
              }}
              onNavigate={onNavigate}
              navDescription={`Mobile Collection: ${collection.name}`}
              className="mobile-collection-link"
            >
              <span className="mobile-collection-icon">{collection.icon}</span>
              <div className="mobile-collection-text">
                <span className="mobile-collection-name">{collection.name}</span>
                <span className="mobile-collection-description">{collection.description}</span>
              </div>
            </MobileNavLink>
          );
        })}
      </div>
    )}
  </div>
);

const MobileNavLink = ({ to, onBaseClick, children, isButton = false, onNavigate, navDescription, className }) => (
  <a
    href={to}
    onClick={(e) => {
      e.preventDefault();
      if (onBaseClick) onBaseClick();
      if (onNavigate) onNavigate(to, navDescription);
    }}
    className={className || `mobile-nav-link ${isButton ? 'mobile-button' : ''}`}
  >
    {children}
  </a>
);

export default Navbar;